import React from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { iRootState } from '../../store';
import { UserRolesEnum } from '../../enums/user-roles.enum';

const Container = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
  background-color: #fff;
`;

const Title = styled.h4`
  color: #333;
  margin-bottom: 8px;
  font-size: 16px;
`;

const ProductTitle = styled.p`
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
`;

const Description = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;

  a {
    color: #59249B;
    text-decoration: underline;
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const OptionCard = styled.div<{ selected?: boolean; disabled?: boolean }>`
  border: 2px solid ${props => (props.selected ? '#59249B' : '#e0e0e0')};
  border-radius: 8px;
  padding: 16px 20px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 280px;
  background-color: ${props => (props.selected ? '#f8f5fc' : '#fff')};
  opacity: ${props => (props.disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${props => (props.disabled ? '#e0e0e0' : '#59249B')};
  }
`;

const RadioCircle = styled.div<{ selected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => (props.selected ? '#59249B' : '#ccc')};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${props => (props.selected ? '#59249B' : 'transparent')};
  }
`;

const OptionText = styled.span`
  font-size: 14px;
  color: #333;
`;

const PriceText = styled.span`
  color: #59249B;
  font-weight: bold;
`;

const FreeText = styled.span`
  color: #666;
  font-weight: bold;
`;

interface LenderInsuranceAddonProps {
  rateValue?: number;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  disabled?: boolean;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const LenderInsuranceAddon: React.FC<LenderInsuranceAddonProps> = ({
  rateValue,
  selected,
  onSelect,
  disabled = false
}) => {
  const userRole = useSelector((state: iRootState) => state.user.role);

  if (userRole === UserRolesEnum.estateQuotation) {
    return null;
  }

  return (
    <Container>
      <Title>Adicionais</Title>
      <ProductTitle>Prestamista HDI Seguros</ProductTitle>
      <Description>
        Garante o pagamento das parcelas em caso de imprevistos, como desemprego ou
        invalidez. Para mais informações,{' '}
        <a href="/documents/prestamista-hdi.pdf" target="_blank" rel="noopener noreferrer">
          clique aqui.
        </a>
      </Description>

      <OptionsContainer>
        <OptionCard
          selected={selected}
          disabled={disabled}
          onClick={() => !disabled && onSelect(true)}
        >
          <RadioCircle selected={selected} />
          <OptionText>
            Quero incluir o Seguro Prestamista.{' '}
            <PriceText>{rateValue ? formatCurrency(rateValue) : 'Valor não configurado'}/mês</PriceText>
          </OptionText>
        </OptionCard>

        <OptionCard
          selected={!selected}
          disabled={disabled}
          onClick={() => !disabled && onSelect(false)}
        >
          <RadioCircle selected={!selected} />
          <OptionText>
            Continuar sem proteção. <FreeText>Sem custo</FreeText>
          </OptionText>
        </OptionCard>
      </OptionsContainer>
    </Container>
  );
};

export default LenderInsuranceAddon;
